import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const handleError = (error) => {
  console.error('Team Node API Error:', error);

  if (error.response?.status === 503) {
    return {
      success: false,
      error: 'Service temporarily unavailable'
    };
  }

  if (error.response?.status === 401) {
    return {
      success: false,
      error: 'Unauthorized - Invalid credentials'
    };
  }

  if (error.response?.status === 400) {
    return {
      success: false,
      error: error.response?.data?.message || 'Bad request - Invalid data provided'
    };
  }

  return {
    success: false,
    error: error.response?.data?.message || error.message || 'Operation failed'
  };
};

/**
 * Assigns a node to multiple teams
 * @param {Object} params - Parameters for the request
 * @param {string} params.nodeId - Unique identifier of the node
 * @param {Array<string>} params.teamIds - List of team IDs to assign the node to
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} Response object with success/error status
 */
export const assignNodeToTeams = async ({ nodeId, teamIds, token }) => {
  try {
    const response = await axios.post(`${BASE_URL}/compute-v2/v2/team/node`, {
      node_id: nodeId,
      team_ids: teamIds
    }, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
        'x-auth-user': token
      }
    });

    // Check if there are errors in the response data
    if (response.data?.errors && Object.keys(response.data.errors).length > 0) {
      const errorMessages = Object.values(response.data.errors);
      return {
        success: false,
        error: errorMessages.join('; '),
        data: response.data,
        status: response.status
      };
    }

    return {
      success: true,
      data: response.data,
      status: response.status,
      message: 'Node assigned to teams successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Assigns all nodes of a team to a user
 * @param {Object} params - Parameters for the request
 * @param {string} params.teamId - Unique identifier of the team
 * @param {string} params.userId - Unique identifier of the user
 * @param {boolean} params.isSudo - Whether to create sudo user in all nodes
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} Response object with success/error status
 */
export const assignAllTeamNodesToUser = async ({ teamId, userId, isSudo, token }) => {
  try {
    const response = await axios.post(`${BASE_URL}/compute-v2/v2/team/${teamId}/node`, {
      user_id: userId,
      is_sudo: isSudo
    }, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: response.data,
      status: response.status,
      message: 'All team nodes assigned to user successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Removes a node from a team
 * @param {Object} params - Parameters for the request
 * @param {string} params.nodeId - Unique identifier of the node
 * @param {string} params.teamId - Unique identifier of the team
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} Response object with success/error status
 */
export const removeNodeFromTeam = async ({ nodeId, teamId, token }) => {
  try {
    const response = await axios.delete(`${BASE_URL}/compute-v2/v2/team/${teamId}/node/${nodeId}`, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: response.data,
      status: response.status,
      message: 'Node removed from team successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Gets all nodes of a team
 * @param {Object} params - Parameters for the request
 * @param {string} params.teamId - Unique identifier of the team
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} Response object with success/error status and node IDs
 */
export const getTeamNodes = async ({ teamId, token }) => {
  try {
    const response = await axios.get(`${BASE_URL}/compute-v2/v1/team/${teamId}/node`, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: {
        nodeIds: response.data.node_ids || response.data.nodeIds || []
      },
      status: response.status,
      message: 'Team nodes retrieved successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};
